import { useDocumentLoadMask } from '@affine/core/blocksuite/block-suite-editor/document-load-mask';
import { ErrorBoundary, type FallbackRender } from '@sentry/react';
import type { ComponentType, FC, PropsWithChildren } from 'react';
import { Fragment, useCallback } from 'react';

import { AffineErrorFallback } from './affine-error-fallback';

export { type FallbackProps } from './error-basic/fallback-creator';

export interface AffineErrorBoundaryProps extends PropsWithChildren {
  height?: number | string;
  className?: string;
  /**
   * Where the error shows. A route passes its view body, so the error takes
   * the document's place and the navigation beside it stays usable.
   */
  fallbackHost?: ComponentType<PropsWithChildren>;
}

/**
 * TODO(@eyhn): Unify with SWRErrorBoundary
 */
export const AffineErrorBoundary: FC<AffineErrorBoundaryProps> = props => {
  const loadMask = useDocumentLoadMask();
  const FallbackHost = props.fallbackHost ?? Fragment;
  const fallbackRender: FallbackRender = useCallback(
    fallbackProps => {
      return (
        <FallbackHost>
          <AffineErrorFallback
            {...fallbackProps}
            height={props.height}
            className={props.className}
          />
        </FallbackHost>
      );
    },
    [FallbackHost, props.height, props.className]
  );

  const onError = useCallback(
    (error: unknown, componentStack?: string) => {
      console.error('Uncaught error:', error, componentStack);
      loadMask?.failed();
    },
    [loadMask]
  );

  return (
    <ErrorBoundary fallback={fallbackRender} onError={onError}>
      {props.children}
    </ErrorBoundary>
  );
};
